import { useState } from 'react';
import { Link } from 'react-router';
import { ehRotaInterna } from '@malgoos/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, CircleCheck, Lock, Plus, Trash2 } from 'lucide-react';
import { Botao, Selo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { Estado } from '../../componentes/estados';
import { Entrada } from '../../componentes/formulario';
import { Confirmar, Drawer } from '../../componentes/sobreposicao';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, patch, post } from '../../nucleo/api';
import { data, dataHora, textoLivre } from '../../nucleo/formato';
import { rotuloEntidade } from '../../nucleo/rotulos';
import { useSessao } from '../../nucleo/sessao';
import { CHAVE, COR_PRIORIDADE, atravessaAprovacao, textoDoVinculo, useRotuloColuna, useRotuloModuloAlvo } from './comum';
import { FormCard } from './FormCard';
import { Markdown } from './Markdown';
import type { Card, Coluna, Criterio, Movimento } from './tipos';

type Ficha = Card & { criterios: Criterio[]; descricao: string | null; movimentos: Movimento[] };

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-tinta-3">{rotulo}</dt>
      <TextoCortado como="dd" className="min-w-0 text-right">
        {children}
      </TextoCortado>
    </div>
  );
}

/** Critérios de aceite como checklist: marcar e incluir salvam na hora (PATCH com a lista inteira). */
function Checklist({ card, podeEditar, aoMudar }: { card: Ficha; podeEditar: boolean; aoMudar: () => Promise<void> }) {
  const { t } = useTranslation('tarefas');
  const toast = useToast();
  const [novo, setNovo] = useState('');
  const salvar = useMutation({
    mutationFn: (criterios: Criterio[]) => patch(`/tarefas/cards/${card.id}`, { criterios }),
    onSuccess: aoMudar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const lista = card.criterios;
  const feitos = lista.filter((c) => c.feito).length;
  return (
    <section aria-labelledby={`criterios-${card.id}`} className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between gap-2">
        <h3 id={`criterios-${card.id}`} className="text-sm font-semibold">
          {t('campos.criterios')}
        </h3>
        {lista.length > 0 && <span className="numero text-xs text-tinta-3">{t('ficha.criteriosFeitos', { feitos, total: lista.length })}</span>}
      </div>
      {lista.length === 0 && <p className="text-sm text-tinta-3">{t('ficha.semCriterios')}</p>}
      <ul className="flex flex-col gap-1">
        {lista.map((c, i) => (
          <li key={c.id} className="flex items-start gap-2 rounded-lg px-1 py-1 hover:bg-sup-2">
            <input
              id={`criterio-${c.id}`}
              type="checkbox"
              checked={c.feito}
              disabled={!podeEditar || salvar.isPending}
              onChange={() => salvar.mutate(lista.map((x) => (x.id === c.id ? { ...x, feito: !x.feito } : x)))}
              className="mt-0.5 size-4 shrink-0 accent-acento"
            />
            <label htmlFor={`criterio-${c.id}`} className={`min-w-0 flex-1 text-sm break-words ${c.feito ? 'text-tinta-3 line-through' : ''}`}>
              {textoLivre(c.texto)}
            </label>
            {podeEditar && (
              <button
                type="button"
                aria-label={t('acoes.removerCriterio', { n: i + 1 })}
                onClick={() => salvar.mutate(lista.filter((x) => x.id !== c.id))}
                className="rounded p-0.5 text-tinta-3 hover:text-perigo"
              >
                <Trash2 className="size-4" aria-hidden />
              </button>
            )}
          </li>
        ))}
      </ul>
      {podeEditar && (
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!novo.trim()) return;
            salvar.mutate([...lista, { id: crypto.randomUUID(), texto: novo.trim(), feito: false }], { onSuccess: () => setNovo('') });
          }}
        >
          <Entrada aria-label={t('campos.novoCriterio')} placeholder={t('campos.novoCriterio')} value={novo} onChange={(e) => setNovo(e.target.value)} maxLength={500} />
          <Botao type="submit" icone={<Plus className="size-4" />} carregando={salvar.isPending}>
            {t('acoes.adicionarCriterio')}
          </Botao>
        </form>
      )}
    </section>
  );
}

/** Ficha do card: mover entre colunas, dados, critérios de aceite, histórico de colunas e o chatter `tarefas.card`. */
export function DrawerCard({ id, colunas, aoFechar }: { id: string; colunas: Coluna[]; aoFechar: () => void }) {
  const { t } = useTranslation('tarefas');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const rotuloColuna = useRotuloColuna();
  const rotuloAlvo = useRotuloModuloAlvo();
  const [arquivando, setArquivando] = useState(false);
  const q = useQuery({ queryKey: [...CHAVE, 'card', id], queryFn: () => get<Ficha>(`/tarefas/cards/${id}`) });
  const podeEditar = s.pode('tarefas.editar');
  const podeAprovar = s.pode('tarefas.aprovar');
  const recarregar = async () => {
    await qc.invalidateQueries({ queryKey: CHAVE });
    await qc.invalidateQueries({ queryKey: ['chatter', 'tarefas.card', id] });
  };
  const mover = useMutation({
    mutationFn: (coluna: Coluna) => post(`/tarefas/cards/${id}/mover`, { coluna_id: coluna.id }),
    onSuccess: async (_r, coluna) => {
      toast.sucesso(t('ficha.movido', { coluna: rotuloColuna(coluna) }));
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  // Pacote 2 (ConcluirTarefa): leva à primeira coluna concluída do quadro, pelo caminho do mover.
  const concluir = useMutation({
    mutationFn: () => post(`/tarefas/cards/${id}/concluir`),
    onSuccess: async () => {
      toast.sucesso(t('ficha.concluida'));
      await recarregar();
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const arquivar = useMutation({
    mutationFn: () => del(`/tarefas/cards/${id}`),
    onSuccess: async () => {
      setArquivando(false);
      aoFechar();
      await qc.invalidateQueries({ queryKey: CHAVE });
    },
    onError: (e) => {
      setArquivando(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const c = q.data;
  const doQuadro = c ? colunas.filter((x) => x.quadro_id === c.quadro_id) : [];
  const concluida = [...doQuadro].sort((a, b) => a.ordem - b.ordem || a.id.localeCompare(b.id)).find((x) => x.etapa === 'concluida');
  const autor = (m: Movimento) => m.autor_nome ? (m.ator_tipo === 'chave_api' ? `${m.autor_nome} (${t('ficha.autorChave')})` : m.autor_nome) : t(m.ator_tipo === 'chave_api' ? 'ficha.autorChave' : 'ficha.autorSistema');

  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      largura="lg"
      titulo={c ? `#${c.numero} ${textoLivre(c.titulo)}` : t('ficha.carregando')}
      rodape={
        c && podeEditar ? (
          <>
            {s.pode('tarefas.excluir') && (
              <Botao variante="perigo" icone={<Archive className="size-4" />} className="mr-auto" onClick={() => setArquivando(true)}>
                {t('acoes.arquivar')}
              </Botao>
            )}
            <Botao variante="fantasma" onClick={aoFechar}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-card">
              {tn('comum.salvar')}
            </Botao>
          </>
        ) : undefined
      }
    >
      <Estado consulta={q}>
        {(card) => (
          <div className="flex flex-col gap-6">
            <div className="flex flex-wrap items-center gap-2">
              <Selo cor="acento">{rotuloColuna({ nome: card.coluna_nome, codigo: card.coluna_codigo })}</Selo>
              <Selo cor={COR_PRIORIDADE[card.prioridade] ?? 'neutro'}>{card.prioridade}</Selo>
              {card.tipo && <Selo cor="ciano">{`${card.tipo} · ${t(`tipos.${card.tipo}`)}`}</Selo>}
              {card.tamanho && <Selo>{t(`tamanhos.${card.tamanho}`)}</Selo>}
              {card.modulo_alvo && <Selo cor="roxo">{rotuloAlvo(card.modulo_alvo)}</Selo>}
              {card.atrasado && <Selo cor="perigo">{t('kanban.atrasado')}</Selo>}
            </div>

            {card.quadro_arquivado && <p className="rounded-lg border border-borda px-3 py-2 text-sm text-tinta-2">{t('ficha.projetoArquivado')}</p>}

            {podeEditar && doQuadro.length > 0 && (
              <div className="flex flex-col gap-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm text-tinta-3">{t('acoes.mover')}</span>
                  {concluida && card.coluna_etapa !== 'concluida' && !(atravessaAprovacao(doQuadro, card.coluna_id, concluida.id) && !podeAprovar) && (
                    <Botao tamanho="sm" icone={<CircleCheck className="size-4" />} carregando={concluir.isPending} onClick={() => concluir.mutate()}>
                      {t('acoes.concluir')}
                    </Botao>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  {doQuadro.map((col) => {
                    const exige = atravessaAprovacao(doQuadro, card.coluna_id, col.id);
                    const bloqueada = exige && !podeAprovar;
                    return (
                      <button
                        key={col.id}
                        type="button"
                        disabled={col.id === card.coluna_id || mover.isPending || bloqueada}
                        title={bloqueada ? t('ficha.aprovacaoAviso') : undefined}
                        aria-current={col.id === card.coluna_id ? 'step' : undefined}
                        onClick={() => mover.mutate(col)}
                        className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium disabled:cursor-not-allowed ${
                          col.id === card.coluna_id ? 'border-2 border-acento-forte bg-acento-suave text-acento-forte' : bloqueada ? 'border-borda text-tinta-3 opacity-60' : 'border-borda text-tinta-2 hover:text-tinta-1'
                        }`}
                      >
                        {col.exige_aprovacao && <Lock className="size-3" aria-hidden />}
                        {rotuloColuna(col)}
                      </button>
                    );
                  })}
                </div>
                {!podeAprovar && doQuadro.some((x) => x.exige_aprovacao) && <p className="text-xs text-tinta-3">{t('ficha.aprovacaoAviso')}</p>}
              </div>
            )}

            <div>
              {card.descricao ? <Markdown fonte={card.descricao} links={card.origem === 'sistema'} /> : <p className="text-sm text-tinta-3">{t('ficha.semDescricao')}</p>}
              <p className="mt-2 text-xs text-tinta-3">{t('ficha.dadoNaoOrdem')}</p>
            </div>

            <Checklist card={card} podeEditar={podeEditar} aoMudar={recarregar} />

            <dl className="divide-y divide-borda">
              <Linha rotulo={t('campos.numero')}>{`#${card.numero}`}</Linha>
              <Linha rotulo={t('campos.origem')}>{t(`origens.${card.origem}`)}</Linha>
              {card.solicitante && <Linha rotulo={t('campos.solicitante')}>{textoLivre(card.solicitante)}</Linha>}
              {card.criador_nome && <Linha rotulo={t('campos.criadoEm')}>{`${data(card.created_at)} · ${t('ficha.criadoPor', { nome: card.criador_nome })}`}</Linha>}
              <Linha rotulo={t('campos.naColunaDesde')}>{dataHora(card.coluna_desde)}</Linha>
              {card.vinculo_entidade && card.vinculo_id && (
                <Linha rotulo={t('campos.vinculo')}>
                  {/* W23 (K11): quem vê o registro vê o título e volta a ele pelo link; os outros, só o tipo e o id (como antes). */}
                  {card.vinculo_visivel && card.vinculo_link && ehRotaInterna(card.vinculo_link) ? (
                    <Link to={card.vinculo_link} title={t('registro.abrirRegistro')} data-vinculo-link className="hover:text-acento-forte">
                      {textoDoVinculo(card.vinculo_entidade, card.vinculo_titulo)}
                    </Link>
                  ) : card.vinculo_visivel && card.vinculo_titulo ? (
                    textoDoVinculo(card.vinculo_entidade, card.vinculo_titulo)
                  ) : (
                    <span className="font-mono text-xs">{t('ficha.vinculoTexto', { entidade: rotuloEntidade(card.vinculo_entidade), id: card.vinculo_id.slice(0, 8) })}</span>
                  )}
                </Linha>
              )}
            </dl>

            <FormCard atual={card} somenteLeitura={!podeEditar} aoSalvar={async () => {
              toast.sucesso(t('ficha.salvo'));
              await recarregar();
            }} />

            <section aria-labelledby={`historico-${card.id}`}>
              <h3 id={`historico-${card.id}`} className="mb-2 text-sm font-semibold">
                {t('campos.historico')}
              </h3>
              <ol className="flex flex-col gap-1.5 border-l border-borda pl-3">
                {card.movimentos.map((m) => (
                  <li key={m.id} className="text-sm">
                    <span className="font-medium">{t('ficha.movidoPor', { coluna: rotuloColuna({ nome: m.para_nome, codigo: m.para_codigo }), autor: autor(m) })}</span>
                    <span className="numero ml-2 text-xs text-tinta-3">{dataHora(m.created_at)}</span>
                  </li>
                ))}
              </ol>
            </section>

            <div className="border-t border-borda pt-4">
              <Chatter entidade="tarefas.card" registroId={card.id} />
            </div>

            <Confirmar
              aberto={arquivando}
              aoFechar={() => setArquivando(false)}
              aoConfirmar={() => arquivar.mutate()}
              titulo={t('ficha.arquivarTitulo')}
              texto={t('ficha.arquivarTexto')}
              perigoso
              carregando={arquivar.isPending}
            />
          </div>
        )}
      </Estado>
    </Drawer>
  );
}
